"use client";
import { HTMLProps, useCallback, useEffect, useMemo, useState } from "react";
import { FeatureSelector, GeoJSONFeature, MapMouseEvent } from "mapbox-gl";
import { useMap } from "react-map-gl/mapbox";
import { useAppStore } from "@/store/app";
import { useDataStore } from "@/store/data";
import { HoveredLocationProps } from "@/utils/types";
import { cn, getLocationName, getValue, getValueKey } from "@/utils/helpers";
import { getFeatureSelector } from "./utils/helpers";
import Credits from "@/components/site/Credits";
import Loader from "@/components/common/Loader";
import MapBase from "./components/MapBase";
import MapHeader from "./components/MapHeader";
import MapTooltip from "./components/MapTooltip";
import MapLegend from "./components/MapLegend";
import MapControls from "./components/MapControls";
import { getSourceId } from "./utils/selectors";
import styles from "./Map.module.css";

export interface MapProps extends HTMLProps<HTMLElement> {};

export default function Map({
	className
} : MapProps) {
	const { default: map } = useMap();
	const controls = useAppStore(store => store.controls);
	const selectedLocation = useAppStore(store => store.selectedLocation);
	const hoveredLocation = useAppStore(store => store.hoveredLocation);
	const setSelectedLocation = useAppStore(store => store.setSelectedLocation);
	const setHoveredLocation = useAppStore(store => store.setHoveredLocation);
	const setHoveredPoint = useAppStore(store => store.setHoveredPoint);
	const getLocation = useDataStore(store => store.getLocation);
	const [mapLoading, setMapLoading] = useState<boolean>(true);
	const [prevHoveredFeature, setPrevHoveredFeature] = useState<FeatureSelector | null>(null);
	const [prevSelectedFeature, setPrevSelectedFeature] = useState<FeatureSelector | null>(null);

	const currentSourceId = useMemo(() =>
		getSourceId(controls)
	, [controls]);
	
	const hoveredFeature = useMemo(() =>
		hoveredLocation ? getFeatureSelector(controls, hoveredLocation) : null
	, [controls, hoveredLocation]);

	const selectedFeature = useMemo(() =>
		selectedLocation ? getFeatureSelector(controls, selectedLocation) : null
	, [controls, selectedLocation]);

	// Set featured state for hovered feature
	useEffect(() => {
		if(hoveredFeature && map?.getSource(hoveredFeature.source)) {
			map.setFeatureState(hoveredFeature, { hovered: true });
		}
		if(prevHoveredFeature && hoveredFeature?.id !== prevHoveredFeature.id && map?.getSource(prevHoveredFeature.source)) {
			map.removeFeatureState(prevHoveredFeature, "hovered");
		}
		setPrevHoveredFeature(hoveredFeature);
	}, [map, hoveredFeature]);

	// Set featured state for selected feature
	useEffect(() => {
		if(selectedFeature && map?.getSource(selectedFeature.source)) {
			map.setFeatureState(selectedFeature, { selected: true });
		}
		if(prevSelectedFeature && selectedFeature?.id !== prevSelectedFeature.id && map?.getSource(prevSelectedFeature.source)) {
			map.removeFeatureState(prevSelectedFeature, "selected");
		}
		setPrevSelectedFeature(selectedFeature);
	}, [map, selectedFeature]);

	const handleMove = useCallback(() => {
		setHoveredLocation(null);
	}, []);

	// Start map loading when source changes
	useEffect(() => {
		setMapLoading(true);
	}, [currentSourceId]);

	// End map loading when in idle state
	useEffect(() => {
		if (!map) return;
		function onIdle() {
			setMapLoading(false);
		}
		map?.on("idle", onIdle);
		return () => {
			map?.off("idle", onIdle);
		}
	}, [map, currentSourceId]);

	const handleMouseMove = useCallback((
		properties?: GeoJSONFeature["properties"],
		point?: MapMouseEvent["point"]
	) => {
		const newId = properties?.id;
		// If location is the same, only update hovered
		if (newId === hoveredLocation?.id) return setHoveredPoint(point);
		// If no location, remove
		if (!newId) return setHoveredLocation(null);

		const valueKey = getValueKey(controls);
		// const location = getLocation(controls.geo, newId);
		setHoveredLocation({
			id: newId,
			name: getLocationName(controls.geo, newId),
			value: getValue(valueKey, properties)
		} as HoveredLocationProps);
		setHoveredPoint(point);
	}, [controls, getLocation]);

	const handleMouseLeave = useCallback(() => {
		setHoveredLocation(null)
	}, []);

	const handleMouseOut = useCallback(() => {
		setHoveredLocation(null)
	}, []);

	const handleClick = useCallback((properties?: GeoJSONFeature["properties"]) => {
		if(properties?.id) {
			setSelectedLocation({
				id: properties?.id,
				name: getLocationName(controls.geo, properties?.id),
			});
		}
		setHoveredLocation(null);
	}, [controls]);

	const handleLoad = useCallback(() => {
		setMapLoading(false);
	}, []);

	return (
		<div
			className={cn(
				styles.Map,
				className
			)}
		>
			<MapHeader />
			<MapBase
				onMove={handleMove}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				onMouseOut={handleMouseOut}
				onClick={handleClick}
				onLoad={handleLoad}
			/>
			<MapTooltip />
			<MapControls />
			<MapLegend />
			<Loader
				loading={mapLoading}
				className={styles.MapLoader}
			/>
			<Credits
				className={styles.MapCredits}
			/>
		</div>
	);
}